/* eslint-disable i18next/no-literal-string -- crash screen is deliberately unlocalized (must render even if i18n failed) */
import { Component, type ReactNode } from 'react';
import { captureException } from './lib/analytics/telemetry';

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary.
 * Catches uncaught render errors anywhere in the tree and displays a visible
 * crash screen instead of a blank page. This is critical for debugging
 * bridge-session flows where a blank page gives no diagnostic information.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Caught render error:', error, info);
    captureException(error, { componentStack: info.componentStack });
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div
          style={{
            background: '#fff',
            color: '#1a1a1a',
            padding: '24px',
            fontFamily: 'monospace',
            fontSize: '13px',
            minHeight: '100vh',
            boxSizing: 'border-box',
          }}
        >
          <h2 style={{ color: '#c0392b', marginTop: 0 }}>App crashed — render error</h2>
          <pre
            style={{
              background: '#fdf2f2',
              border: '1px solid #e8c0c0',
              borderRadius: '4px',
              padding: '12px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {String(error)}
            {'\n\n'}
            {error.stack ?? '(no stack)'}
          </pre>
          <p style={{ color: '#555', fontSize: '12px' }}>
            Open browser DevTools → Console for more details. URL: {window.location.href}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
